import ABTestUtility from '../utilities/ABTestUtility.js';
import AnalyticsManager from '../utilities/AnalyticsManager.js';
import ReporterManager from '../utilities/ReporterManager.js';
import Version from '../version.js';
/**
 * Base class for analytics trackers.
 * Handles the common connect / disconnect logic.
 */
export default class AbstractAnalyticsTracker {
    /**
     * @param options ...
     * @param options.moduleId The id of the module (for identification of events).
     * @param options.dev Should be true if application is in development mode, defaults to false.
     * @param options.ab If ab tests should be enabled or not.
     * @param options.settings DEXF settings needed for Cookie Consent service and AB tests.
     * @param options.locale Locale needed for Cookie Consent service.
     * @param options.reporterId The id of an already connected reporter instance which should be used by this tracker.
     * @param kioskLongTermId Should be supplied when an A/B test is run exclusively on kiosk.
     */
    constructor(options, kioskLongTermId) {
        this.reporter = ReporterManager.getReporter(options.reporterId);
        this.options = options;
        const { ab, settings } = options;
        this.abTest = ab ? ABTestUtility.initializeABTests(settings, kioskLongTermId) : null;
        this.managesReporterLifecycle = !this.reporter.isConnected();
    }
    /**
     * Connects the reporter used by the tracker.
     * If the reporter is managed by another tracker, nothing will happen. The reporter is already connected and can be reused by this tracker.
     *
     * The trackers are responsible for the reporters' lifecycles, that is connecting and disconnecting them.
     * A reporter can be used by several trackers, but it should only be connected and disconnected once. This is the responsibility of the tracker of top level component.
     *
     * Example:
     * - Component_A has support for analytics.
     * - Component_B has support for analytics.
     * - Component_B can be rendered both from within Component_A and can be used stand alone.
     *
     * Scenarios:
     * - Component_A calls Tracker_A.connect. Tracker_A manages the reporter lifecycle, the reporter will be connected.
     * - Component_B (as stand alone) calls Tracker_B.connect. Tracker_B manages the reporter lifecycle, the reporter will be connected.
     * - Component_B (within Component_A) calls Tracker_B.connect. Tracker_A manages the reporter lifecycle, nothing will happen, the reporter is already connected.
     *
     */
    async connect() {
        if (!this.managesReporterLifecycle) {
            return;
        }
        const insightsApi = AnalyticsManager.getInsightsApi();
        const { moduleId, dev, locale, settings } = this.options;
        const cookieConsentServiceOptions = { locale, settings: settings };
        return this.reporter.connect(insightsApi, {
            moduleId,
            moduleVersion: Version.version,
            dev,
            abVersionName: this.abTest?.name,
            abVersionVariation: this.abTest?.variation
        }, cookieConsentServiceOptions);
    }
    /**
     * Disconnects the reporter used by tracker.
     * If the reporter is managed by another tracker, nothing will happen. The reporter can be used by several trackers and it should not be disconnected while still in use by another tracker.
     *
     * See {@link connect} for more information.
     */
    disconnect() {
        if (this.managesReporterLifecycle) {
            this.reporter.disconnect();
        }
    }
    /**
     * Get the id used to retrieve the connected reporter or null if the reporter is not connected yet.
     *
     * @returns The id or null.
     */
    getConnectedReporterId() {
        return this.reporter.getConnectedId();
    }
    /**
     * @returns AB test name and variation if there is an active test available in Översätta.
     */
    getABTest() {
        return this.abTest ?? null;
    }
    /**
     * Returns the reporter.
     */
    getReporter() {
        return this.reporter;
    }
}
//# sourceMappingURL=AbstractAnalyticsTracker.js.map